import { FC, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal, Form, Input, Select, FormInstance, Tooltip } from 'antd'
import { ButtonsBlock } from '../../../Edit/components'
import style from './style.m.less'
import { QuestionCircleOutlined } from '@ant-design/icons'
import { formatNumber, restrictInput } from '../../helpers'

interface IAgentsModalProps {
    isModalVisible: boolean
    setIsModalVisible: (value: boolean) => void
    calcForm: FormInstance
    categories: Array<{
        id: number
        name: string
        coeff: number
        name_en: string
    }>
    agentsData: any[]
    onSubmit: (values: { agentVolume: string; categoryId: number; emissionCoeff: number }) => void
    locale: string
}

const AgentsModal: FC<IAgentsModalProps> = ({
    isModalVisible,
    setIsModalVisible,
    calcForm,
    categories,
    agentsData,
    onSubmit,
    locale,
}) => {
    const { t } = useTranslation()
    const [form] = Form.useForm()
    const [selectedCategory, setSelectedCategory] = useState<number | null>(null)

    useEffect(() => {
        if (isModalVisible) {
            form.resetFields()
            setSelectedCategory(null)
        }
    }, [isModalVisible, form])

    const getMaxAvailableVolume = () => {
        const mainVolume = parseFloat(
            (calcForm.getFieldValue('volume') || '0').replaceAll(' ', '').replace(',', '.'),
        )

        const agentsSum = agentsData.reduce((sum, agent) => {
            return sum + parseFloat(agent.volume.replaceAll(' ', '').replace(',', '.'))
        }, 0)

        return mainVolume - agentsSum
    }
    const handleCategoryChange = (value: number) => {
        setSelectedCategory(value)
        // Если переключились не на 8-12 - ставим 0
        if (!(value >= 8 && value <= 12)) {
            form.setFieldsValue({ emissionCoeff: 0 })
        }
        // Если переключились на категорию 8-12 - очищаем поле
        else if (value >= 8 && value <= 12) {
            form.setFieldsValue({ emissionCoeff: undefined })
        }
    }

    const handleSubmit = () => {
        form.validateFields()
            .then((values) => {
                onSubmit({
                    agentVolume: values.agentVolume,
                    categoryId: values.category,
                    emissionCoeff: values.emissionCoeff,
                })
                setIsModalVisible(false)
            })
            .catch((info) => {
                console.log('Validate Failed:', info)
            })
    }

    const handleCancel = () => {
        setIsModalVisible(false)
    }

    const formatAgentVolume = (value: string) => {
        if (!value || value === '') {
            return
        }
        if (value[value.length - 1] === '.' || value[value.length - 1] === ',') {
            return
        }

        const formatted = formatNumber(value)

        form.setFieldsValue({ agentVolume: formatted })
    }

    return (
        <Modal
            closable={false}
            visible={isModalVisible}
            onCancel={handleCancel}
            width={548}
            footer={null}
            title={null}
            className={style.modalBody}
            bodyStyle={{ borderRadius: '24px', padding: 20 }}
            style={{ borderRadius: '24px' }}
        >
            <Form
                form={form}
                layout="vertical"
                initialValues={{ emissionCoeff: 0 }}
                className={style.formBody}
            >
                <div className={style.formHeader}>{t('calculator.modal.title')}</div>
                <Form.Item
                    label={
                        <span>
                            {t('calculator.modal.volume.label')}
                            <Tooltip
                                title={t('calculator.modal.volume.tooltip')}
                                overlayStyle={{ maxWidth: 600 }}
                            >
                                <QuestionCircleOutlined
                                    style={{ marginLeft: 8, color: 'rgba(0, 0, 0, 0.45)' }}
                                />
                            </Tooltip>
                        </span>
                    }
                    name="agentVolume"
                    rules={[
                        { required: true, message: t('calculator.modal.volume.error.required') },
                        {
                            validator: (_, value) => {
                                const max = getMaxAvailableVolume()
                                const curValue = parseFloat(
                                    (value ? value : '0').replaceAll(' ', '').replace(',', '.'),
                                )
                                if (curValue > max) {
                                    return Promise.reject(
                                        `${t('calculator.modal.volume.error.max')} ${max}`,
                                    )
                                }
                                return Promise.resolve()
                            },
                        },
                    ]}
                >
                    <Input
                        className={style.formInput}
                        onKeyDown={restrictInput}
                        onChange={(e) => formatAgentVolume(e.target.value)}
                    />
                </Form.Item>

                <Form.Item
                    label={t('calculator.modal.category.label')}
                    name="category"
                    rules={[
                        {
                            required: true,
                            message: t('calculator.modal.category.error'),
                        },
                    ]}
                >
                    <Select onChange={handleCategoryChange} showSearch className={style.formInput}>
                        {categories.map((category) => (
                            <Select.Option key={category.id} value={category.id}>
                                {locale === 'ru' ? category.name : category.name_en}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item
                    label={
                        <span>
                            {t('calculator.modal.emissionCoeff.label')}
                            <Tooltip
                                title={t('calculator.modal.emissionCoeff.tooltip')}
                                overlayStyle={{ maxWidth: 800 }}
                            >
                                <QuestionCircleOutlined
                                    style={{ marginLeft: 8, color: 'rgba(0, 0, 0, 0.45)' }}
                                />
                            </Tooltip>
                        </span>
                    }
                    name="emissionCoeff"
                    rules={[
                        {
                            required: true,
                            message: t('calculator.modal.emissionCoeff.error'),
                        },
                    ]}
                >
                    <Input
                        className={style.formInput}
                        type="number"
                        disabled={
                            selectedCategory !== null &&
                            !(selectedCategory >= 8 && selectedCategory <= 12)
                        }
                    />
                </Form.Item>

                <div className={style.buttonsBlock}>
                    <ButtonsBlock
                        confirmAction={handleSubmit}
                        cancelAction={handleCancel}
                        confirmTitle={t('calculator.modal.addButton')}
                    />
                </div>
            </Form>
        </Modal>
    )
}

export default AgentsModal
